import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    companyName: "",
    companyType: "",
    message: ""
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone || !formData.companyName || !formData.companyType) {
      toast.error("Please fill in all required fields.", { duration: 3000 });
      return;
    }

    if (!formData.email.includes('@')) {
      toast.error("Please enter a valid email address.", { duration: 3000 });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/demo-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, source: 'contact_page' })
      });
      const result = await response.json();
      if (response.ok) {
        toast.success("Thank you for reaching out! We'll get back to you within 24 hours.", {
          duration: 5000,
        });
        setFormData({ name: "", email: "", phone: "", companyName: "", companyType: "", message: "" });
      } else {
        toast.error(result.message || "Something went wrong. Please try again.", { duration: 4000 });
      }
    } catch (error) {
      toast.error("Network error. Please try again.", { duration: 4000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleTypeChange = (value) => {
    setFormData({ ...formData, companyType: value });
  };

  return (
    <>
      {/* Hero */}
      <section className="pt-28 pb-8 sm:pt-32 sm:pb-10 md:pt-36 md:pb-12 px-4 sm:px-6" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.p 
              variants={fadeInUp}
              className="text-xs tracking-[0.15em] uppercase font-bold text-[#8A9A86] mb-3"
            >
              Contact
            </motion.p>
            <motion.h1 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-[#1A362D] font-medium mb-4"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Get in Touch
            </motion.h1>
            <motion.p 
              variants={fadeInUp}
              className="text-base sm:text-lg text-[#5C6C65] max-w-xl mx-auto"
            >
              Have questions about Praqio? Want to schedule a demo? 
              We'd love to hear from you.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-8 sm:py-10 md:py-12 px-4 sm:px-6" data-testid="contact-content">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-white rounded-2xl p-6 sm:p-8 border border-[#E2E8D8]">
                <h2 
                  className="text-xl font-medium text-[#1A362D] mb-5"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Request a Demo
                </h2>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <Label htmlFor="name" className="text-[#1A362D] font-medium text-sm">
                      Full Name *
                    </Label>
                    <Input 
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Smith"
                      className="mt-1.5 rounded-xl border-[#E2E8D8] focus:border-[#1A362D]"
                      data-testid="contact-name"
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email" className="text-[#1A362D] font-medium text-sm">
                        Email *
                      </Label>
                      <Input 
                        id="email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@practice.com"
                        className="mt-1.5 rounded-xl border-[#E2E8D8] focus:border-[#1A362D]"
                        data-testid="contact-email"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-[#1A362D] font-medium text-sm">
                        Phone *
                      </Label>
                      <Input 
                        id="phone"
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(555) 123-4567"
                        className="mt-1.5 rounded-xl border-[#E2E8D8] focus:border-[#1A362D]"
                        data-testid="contact-phone"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="companyName" className="text-[#1A362D] font-medium text-sm">
                      Practice / Company Name *
                    </Label>
                    <Input 
                      id="companyName"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      placeholder="ABC Medical Group"
                      className="mt-1.5 rounded-xl border-[#E2E8D8] focus:border-[#1A362D]"
                      data-testid="contact-company"
                    />
                  </div>
                  <div>
                    <Label className="text-[#1A362D] font-medium text-sm">
                      Company Type *
                    </Label>
                    <Select onValueChange={handleTypeChange} value={formData.companyType}>
                      <SelectTrigger 
                        className="mt-1.5 rounded-xl border-[#E2E8D8] focus:border-[#1A362D]"
                        data-testid="contact-type"
                      >
                        <SelectValue placeholder="Select your company type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="provider">Healthcare Provider / Practice</SelectItem>
                        <SelectItem value="billing">Billing Company</SelectItem>
                        <SelectItem value="credentialing">Credentialing Company</SelectItem>
                        <SelectItem value="hospital">Hospital / Health System</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="message" className="text-[#1A362D] font-medium text-sm">
                      Message (Optional)
                    </Label>
                    <Textarea 
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your credentialing needs..."
                      rows={4}
                      className="mt-1.5 rounded-xl border-[#E2E8D8] focus:border-[#1A362D] resize-none"
                      data-testid="contact-message"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full btn-pill btn-primary py-5"
                    data-testid="contact-submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Submitting..." : "Submit Request"}
                    <Send className="ml-2 h-4 w-4" />
                  </Button>
                </form>
              </div>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div>
                <h2 
                  className="text-xl font-medium text-[#1A362D] mb-4"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Contact Information
                </h2>
                <p className="text-[#5C6C65] text-sm mb-6">
                  Our team is available Monday through Friday, 9am to 6pm EST. 
                  We typically respond within 24 hours.
                </p>
              </div>

              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#1A362D] flex items-center justify-center flex-shrink-0">
                    <Mail className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h3 className="font-medium text-[#1A362D] text-sm mb-1">Email</h3>
                    <p className="text-sm text-[#5C6C65]">hello@praqio.com</p>
                    <p className="text-sm text-[#5C6C65]">support@praqio.com</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#1A362D] flex items-center justify-center flex-shrink-0">
                    <Phone className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h3 className="font-medium text-[#1A362D] text-sm mb-1">Phone</h3>
                    <p className="text-sm text-[#5C6C65]">877-614-5227</p>
                    <p className="text-xs text-[#8A9A86]">Mon-Fri, 9am-6pm EST</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#1A362D] flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h3 className="font-medium text-[#1A362D] text-sm mb-1">Office</h3>
                    <p className="text-sm text-[#5C6C65]">Capital Billing Services, Inc.</p>
                    <p className="text-sm text-[#5C6C65]">2275 Marietta Blvd NW Ste 270-131</p>
                    <p className="text-sm text-[#5C6C65]">Atlanta, GA 30318</p>
                  </div>
                </div>
              </div>

              {/* Company Info Card */}
              <div className="bg-[#1A362D] rounded-2xl p-6 mt-6">
                <h3 
                  className="text-lg font-medium text-white mb-3"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  About Capital Billing Services, Inc.
                </h3>
                <p className="text-sm text-[#8A9A86] mb-4">
                  Praqio is proudly built by Capital Billing Services, Inc., a healthcare technology 
                  company dedicated to simplifying credentialing workflows for 
                  practices of all sizes.
                </p>
                <div className="pt-4 border-t border-white/10">
                  <p className="text-xs text-[#8A9A86]">
                    HIPAA Compliant • SOC 2 Type II Certified
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}
